import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiCopy,
  FiInfo,
  FiSearch,
  FiShield,
} from "react-icons/fi";
import type { PayoutAccount } from "./payoutData";
import PayoutBeneficiaryFlowSteps from "./PayoutBeneficiaryFlowSteps";
import { writeSessionJson, errorMessage, stableIdempotencyKey } from "./api/payoutMutationHelpers";
import { isPayoutApiMode, payoutApi, ensurePayoutsAccessToken } from "./api/payoutApiClient";
import { accountFromApiRecord } from "./api/payoutApiAdapters";
import { usePayoutResource } from "./api/usePayoutResource";
import {
  normalizeBankAccountNumber,
  normalizeEmail,
  normalizeIfsc,
  normalizeIndianMobile,
  validateRequiredBankAccountNumber,
  validateRequiredConfirmAccountNumber,
  validateRequiredEmail,
  validateRequiredIfsc,
  validateRequiredIndianMobile,
} from "./api/payoutValidation";
import { showPayoutNotification } from "./PayoutNotification";
import { contextualValidationMessage } from "./payoutAlertMessages";
/** Known PNB UAT sample used for local name-check when live bank mTLS is unavailable. */
const LOCAL_UAT_BENE = {
  account: "0153009500289697",
  ifscPrefix: "PUNB",
  name: "SANTOSH KUMAR",
};

/** Bharat / Trishvi Postman fixture (seed + transfer UAT). */
const BHARAT_UAT_BENE = {
  account: "123456789012",
  ifsc: "HDFC0001782",
  name: "",
};

const emptyPayoutAccount: PayoutAccount = {
  id: "",
  name: "",
  school: "",
  bank: "",
  logoUrl: "",
  accountNumber: "",
  ifsc: "",
  currency: "INR",
  addedOn: "",
  availableBalance: 0,
  accountBalance: 0,
  fundsOnHold: 0,
  overdraftBalance: 0,
  overdraftLimit: 0,
  lowBalanceThreshold: 0,
  accent: "bg-[#6687FF]",
};

const namesMatch = (a: string, b: string) => {
  const left = a.trim().toUpperCase().replace(/\s+/g, " ");
  const right = b.trim().toUpperCase().replace(/\s+/g, " ");
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
};

function FieldLabel({
  children,
  required = false,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <span className="mb-2 block text-xs font-semibold text-[#505E8E]">
      {children}
      {required && <span className="text-[#EF4444]"> *</span>}
    </span>
  );
}

function TextInput({
  label,
  placeholder,
  required = false,
  rightText,
  value,
  onChange,
  error,
  inputMode,
  maxLength,
}: {
  label: string;
  placeholder: string;
  required?: boolean;
  rightText?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
}) {
  return (
    <label className="block">
      <FieldLabel required={required}>{label}</FieldLabel>
      <div
        className={
          "flex h-11 items-center rounded-[6px] border bg-white px-3 " +
          (error ? "border-[#EF4444]" : "border-[#E0E5F2]")
        }
      >
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          inputMode={inputMode}
          maxLength={maxLength}
          className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
          placeholder={placeholder}
        />
        {rightText && (
          <button className="shrink-0 text-sm font-semibold text-[#6687FF]">
            {rightText}
          </button>
        )}
      </div>
      {error && (
        <p className="mt-2 text-xs font-semibold text-[#EF4444]">{error}</p>
      )}
    </label>
  );
}

type SelectOption = {
  label: string;
  value: string;
  meta?: string;
  icon?: string;
};

function SearchableDropdown({
  label,
  placeholder,
  options,
  searchPlaceholder = "Search options",
  required = false,
  value,
  onChange,
  error,
}: {
  label: string;
  placeholder: string;
  options: SelectOption[];
  searchPlaceholder?: string;
  required?: boolean;
  value: SelectOption | null;
  onChange: (value: SelectOption) => void;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filteredOptions = options.filter((option) =>
    (option.label + " " + (option.meta || ""))
      .toLowerCase()
      .includes(query.toLowerCase()),
  );

  return (
    <div className="relative">
      <FieldLabel required={required}>{label}</FieldLabel>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={
          "flex h-11 w-full items-center justify-between rounded-[6px] border bg-white px-3 text-left text-sm font-semibold outline-none " +
          (error ? "border-[#EF4444]" : open ? "border-[#6687FF]" : "border-[#E0E5F2]")
        }
      >
        <span
          className={
            "flex min-w-0 items-center gap-2 " +
            (value ? "text-[#1B163B]" : "text-[#A3AED0]")
          }
        >
          {value?.icon && <span>{value.icon}</span>}
          <span className="truncate">{value?.label || placeholder}</span>
        </span>
        <FiChevronDown
          className={
            "shrink-0 text-[#505E8E] transition-transform " +
            (open ? "rotate-180" : "")
          }
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white new-shadow">
          <div className="flex h-10 items-center gap-2 border-b border-[#E0E5F2] px-3">
            <FiSearch className="shrink-0 text-[#505E8E]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
              placeholder={searchPlaceholder}
            />
          </div>
          <div className="max-h-[220px] overflow-y-auto py-1">
            {filteredOptions.map((option) => {
              const active = value?.value === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={
                    "flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-medium hover:bg-[#A9B2CF1A] " +
                    (active ? "text-[#1B163B]" : "text-[#505E8E]")
                  }
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {option.icon && <span>{option.icon}</span>}
                    <span className="truncate">
                      {option.label}
                      {option.meta && (
                        <span className="ml-1 text-[#505E8E]">
                          {option.meta}
                        </span>
                      )}
                    </span>
                  </span>
                  {active && <FiCheck className="shrink-0 text-[#6687FF]" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {error && (
        <p className="mt-2 text-xs font-semibold text-[#EF4444]">{error}</p>
      )}
    </div>
  );
}

const beneficiaryCategoryOptions = [
  { label: "Vendor", value: "vendor" },
  { label: "Employee", value: "employee" },
  { label: "Student", value: "student" },
  { label: "Scholarship", value: "scholarship" },
  { label: "Refund", value: "refund" },
  { label: "Reimbursement", value: "reimbursement" },
  { label: "Transport Vendor", value: "transport-vendor" },
  { label: "Utility Provider", value: "utility-provider" },
  { label: "Others", value: "others" },
];

const countryCodeOptions = [
  { label: "India", value: "+91", meta: "(+91)", icon: "🇮🇳" },
  { label: "United States", value: "+1", meta: "(+1)", icon: "🇺🇸" },
  { label: "United Kingdom", value: "+44", meta: "(+44)", icon: "🇬🇧" },
  { label: "Australia", value: "+61", meta: "(+61)", icon: "🇦🇺" },
  { label: "United Arab Emirates", value: "+971", meta: "(+971)", icon: "🇦🇪" },
  { label: "Singapore", value: "+65", meta: "(+65)", icon: "🇸🇬" },
  { label: "Germany", value: "+49", meta: "(+49)", icon: "🇩🇪" },
  { label: "South Africa", value: "+27", meta: "(+27)", icon: "🇿🇦" },
];

const accountTypeOptions = [
  { label: "Savings", value: "savings" },
  { label: "Current", value: "current" },
  { label: "Salary", value: "salary" },
  { label: "NRE", value: "nre", meta: "Non-resident external" },
  { label: "NRO", value: "nro", meta: "Non-resident ordinary" },
  { label: "Overdraft", value: "overdraft" },
  { label: "Cash Credit", value: "cash-credit" },
];

function CountryCodeDropdown() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(countryCodeOptions[0]);
  const filteredOptions = countryCodeOptions.filter((option) =>
    (option.label + " " + option.value)
      .toLowerCase()
      .includes(query.toLowerCase()),
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={
          "flex h-11 w-full items-center justify-between rounded-[6px] border bg-white px-3 text-sm font-semibold text-[#1B163B] " +
          (open ? "border-[#6687FF]" : "border-[#E0E5F2]")
        }
      >
        <span className="flex items-center gap-2">
          <span>{selected.icon}</span>
          <span>{selected.value}</span>
        </span>
        <FiChevronDown
          className={
            "text-[#505E8E] transition-transform " + (open ? "rotate-180" : "")
          }
        />
      </button>

      {open && (
        <div className="absolute left-0 z-40 mt-1 w-[280px] overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white new-shadow">
          <div className="flex h-10 items-center gap-2 border-b border-[#E0E5F2] px-3">
            <FiSearch className="shrink-0 text-[#505E8E]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
              placeholder="Search for countries"
            />
          </div>
          <div className="max-h-[220px] overflow-y-auto py-1">
            {filteredOptions.map((option) => {
              const active = selected.value === option.value;

              return (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => {
                    setSelected(option);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={
                    "flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-medium hover:bg-[#A9B2CF1A] " +
                    (active ? "text-[#1B163B]" : "text-[#505E8E]")
                  }
                >
                  <span className="flex items-center gap-2">
                    <span>{option.icon}</span>
                    <span>
                      {option.label} {option.meta}
                    </span>
                  </span>
                  {active && <FiCheck className="text-[#6687FF]" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryLine({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-between py-2 text-sm">
      <span className="font-medium text-[#505E8E]">{label}</span>
      <span className="font-semibold text-[#505E8E]">-</span>
    </div>
  );
}

function PayoutAddBeneficiary() {
  const navigate = useNavigate();
  const [payoutAccount, setPayoutAccount] = useState<SelectOption | null>(null);
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [category, setCategory] = useState<SelectOption | null>(null);
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccountNumber, setConfirmAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountType, setAccountType] = useState<SelectOption | null>(null);
  const [branchName, setBranchName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [tags, setTags] = useState("");
  const [suggestedBeneficiaryName, setSuggestedBeneficiaryName] = useState("");
  const [beneficiaryNameVerified, setBeneficiaryNameVerified] = useState(false);
  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const accountsResource = usePayoutResource(
    [] as PayoutAccount[],
    (signal) =>
      payoutApi.accounts(signal).then((rows) =>
        rows.map((row) => accountFromApiRecord(row, emptyPayoutAccount)),
      ),
  );
  const linkedAccounts =
    isPayoutApiMode() && !accountsResource.usingFallback && accountsResource.data.length
      ? accountsResource.data
      : [];
  const payoutBankAccountOptions = useMemo(
    () =>
      linkedAccounts.map((account) => ({
        label: account.name,
        value: account.id,
        meta: `${account.bank} **** ${account.accountNumber}`,
      })),
    [linkedAccounts],
  );

  useEffect(() => {
    if (payoutAccount || payoutBankAccountOptions.length !== 1) return;
    setPayoutAccount(payoutBankAccountOptions[0]);
  }, [payoutAccount, payoutBankAccountOptions]);

  useEffect(() => {
    if (!isPayoutApiMode()) return;
    ensurePayoutsAccessToken().catch((err) => {
      console.warn("Payouts auth exchange failed on add beneficiary", err);
    });
  }, []);

  const clearError = (field: string) => {
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const applyLocalUatNameCheck = (name: string, acct: string, ifsc: string) => {
    const normalizedIfsc = ifsc.trim().toUpperCase();
    const isBharatUat =
      acct === BHARAT_UAT_BENE.account && normalizedIfsc === BHARAT_UAT_BENE.ifsc;
    if (isBharatUat) {
      if (namesMatch(name, BHARAT_UAT_BENE.name)) {
        setBeneficiaryName(BHARAT_UAT_BENE.name);
        setBeneficiaryNameVerified(true);
        setSuggestedBeneficiaryName("");
        clearError("beneficiaryName");
        return true;
      }
      setBeneficiaryNameVerified(false);
      setSuggestedBeneficiaryName(BHARAT_UAT_BENE.name);
      setErrors((current) => ({
        ...current,
        beneficiaryName: "Name does not match the bank records.",
      }));
      return true;
    }

    const isKnownPnbUat =
      acct === LOCAL_UAT_BENE.account && normalizedIfsc.startsWith(LOCAL_UAT_BENE.ifscPrefix);
    if (!isKnownPnbUat) {
      setBeneficiaryNameVerified(false);
      setSuggestedBeneficiaryName("");
      setErrors((current) => ({
        ...current,
        beneficiaryName:
          "Bank name enquiry is unavailable. Use Bharat UAT account 123456789012 / HDFC0001782 or connect payouts API.",
      }));
      return false;
    }

    if (namesMatch(name, LOCAL_UAT_BENE.name)) {
      setBeneficiaryName(LOCAL_UAT_BENE.name);
      setBeneficiaryNameVerified(true);
      setSuggestedBeneficiaryName("");
      clearError("beneficiaryName");
      return true;
    }

    setBeneficiaryNameVerified(false);
    setSuggestedBeneficiaryName(LOCAL_UAT_BENE.name);
    setErrors((current) => ({
      ...current,
      beneficiaryName: "Name does not match bank records. Use the suggested name.",
    }));
    return true;
  };

  const handleCheckBeneficiaryName = async () => {
    const name = beneficiaryName.trim();
    const acct = normalizeBankAccountNumber(accountNumber);
    const ifsc = normalizeIfsc(ifscCode);

    if (!name) {
      setErrors((current) => ({
        ...current,
        beneficiaryName: "Enter beneficiary name before verification.",
      }));
      return;
    }
    if (!acct || !ifsc) {
      setErrors((current) => ({
        ...current,
        beneficiaryName: "Enter account number and IFSC before name check.",
      }));
      return;
    }

    setNameCheckLoading(true);
    try {
      const result = await payoutApi.beneficiaryNameCheck(
        { account_number: acct, ifsc, name },
        stableIdempotencyKey("bene-name-check", `${acct}-${ifsc}-${name}`),
      );
      const bankName =
        typeof result.name_as_per_bank === "string"
          ? result.name_as_per_bank.trim()
          : "";
      const matched = Boolean(result.matched);

      if (matched && bankName) {
        setBeneficiaryName(bankName);
        setBeneficiaryNameVerified(true);
        setSuggestedBeneficiaryName("");
        clearError("beneficiaryName");
        return;
      }

      if (bankName) {
        setBeneficiaryNameVerified(false);
        setSuggestedBeneficiaryName(bankName);
        setErrors((current) => ({
          ...current,
          beneficiaryName: "Name does not match bank records. Use the suggested name.",
        }));
        return;
      }

      // Live PNB name enquiry unavailable (e.g. mTLS). Use known UAT fixture only.
      applyLocalUatNameCheck(name, acct, ifsc);
    } catch (error) {
      if (!applyLocalUatNameCheck(name, acct, ifsc)) {
        setBeneficiaryNameVerified(false);
        setErrors((current) => ({
          ...current,
          beneficiaryName: errorMessage(error, "Unable to verify beneficiary name."),
        }));
      }
    } finally {
      setNameCheckLoading(false);
    }
  };

  const handleCopyBeneficiaryName = async () => {
    if (!suggestedBeneficiaryName) return;
    try {
      await navigator.clipboard?.writeText(suggestedBeneficiaryName);
    } catch {
      // Clipboard can be unavailable in non-secure local contexts.
    }
    setBeneficiaryName(suggestedBeneficiaryName);
    setSuggestedBeneficiaryName("");
    setBeneficiaryNameVerified(true);
    clearError("beneficiaryName");
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};
    const trimmedMobile = normalizeIndianMobile(mobileNumber);
    const trimmedEmail = normalizeEmail(email);
    const trimmedAccount = normalizeBankAccountNumber(accountNumber);
    const trimmedConfirmAccount = normalizeBankAccountNumber(confirmAccountNumber);
    const normalizedIfsc = normalizeIfsc(ifscCode);

    if (!payoutAccount) {
      nextErrors.payoutAccount = "Select the payout bank account.";
    }
    if (!beneficiaryName.trim()) {
      nextErrors.beneficiaryName = "Enter beneficiary name.";
    } else if (beneficiaryName.trim().length < 3) {
      nextErrors.beneficiaryName = "Beneficiary name must be at least 3 characters.";
    } else if (!beneficiaryNameVerified) {
      nextErrors.beneficiaryName = "Verify beneficiary name as per bank records.";
    }
    if (!category) {
      nextErrors.category = "Select beneficiary category.";
    }
    nextErrors.mobileNumber = validateRequiredIndianMobile(mobileNumber);
    nextErrors.email = validateRequiredEmail(email);
    nextErrors.accountNumber = validateRequiredBankAccountNumber(accountNumber);
    nextErrors.confirmAccountNumber = validateRequiredConfirmAccountNumber(
      confirmAccountNumber,
      accountNumber,
    );
    nextErrors.ifscCode = validateRequiredIfsc(ifscCode);
    if (!accountType) {
      nextErrors.accountType = "Select account type.";
    }
    if (!branchName.trim()) {
      nextErrors.branchName = "Enter branch name.";
    }

    const compactErrors = Object.fromEntries(
      Object.entries(nextErrors).filter(([, value]) => value),
    ) as Record<string, string>;
    setErrors(compactErrors);

    if (!Object.keys(compactErrors).length) {
      writeSessionJson("payoutBeneficiaryDraft", {
        payoutAccount,
        beneficiaryName: beneficiaryName.trim(),
        category,
        mobileNumber: trimmedMobile,
        email: trimmedEmail,
        accountNumber: trimmedAccount,
        confirmAccountNumber: trimmedConfirmAccount,
        ifscCode: normalizedIfsc,
        accountType,
        branchName: branchName.trim(),
        remarks: remarks.trim(),
        tags: tags.trim(),
        beneficiaryNameVerified,
      });
      navigate("/payouts/beneficiaries/add/review");
    } else {
      showPayoutNotification({
        tone: "error",
        message: contextualValidationMessage(
          "Add beneficiary",
          compactErrors,
          "Add beneficiary: complete the highlighted beneficiary and bank details.",
        ),
      });
    }
  };

  useEffect(() => {
    if (!isPayoutApiMode() || !accountsResource.error) return;
    showPayoutNotification({
      tone: "error",
      message: `Cannot load payout accounts: ${accountsResource.error}.`,
    });
  }, [accountsResource.error]);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Add Beneficiary
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Add a new beneficiary to initiate payout transfers
          </p>
          {isPayoutApiMode() && (
            <p className="mt-2 text-xs font-medium text-[#505E8E]">
              Save saves to session only. The API call runs on step 3 (Verify → Continue).
            </p>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/payouts/beneficiaries"
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#6687FF]"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={validateForm}
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
            className="flex h-10 items-center gap-4 rounded-[6px] bg-edviron_black px-7 text-sm font-semibold text-white shadow-sm hover:bg-edviron_black/90"
          >
            Save & Continue
            <FiArrowRight className="text-lg" />
          </button>
        </div>
      </div>

      <PayoutBeneficiaryFlowSteps activeStep={1} />

      {false && (
        <div className="mb-5 rounded-[6px] border border-[#EF444433] bg-[#FEE2E2] px-5 py-4 text-sm font-semibold text-[#EF4444]">
          Cannot load payout accounts: {accountsResource.error}. Re-open{" "}
          <code className="text-xs">/admin?token=…</code> and ensure payouts-service is on :4100.
        </div>
      )}

      {false && (
        <div className="mb-5 rounded-[6px] border border-[#EF444433] bg-[#FEE2E2] px-5 py-4 text-sm font-semibold text-[#EF4444]">
          Add beneficiary: complete the highlighted beneficiary and bank details.
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_430px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-7 new-shadow">
          <h2 className="mb-7 text-[18px] font-semibold text-[#1B163B]">
            Beneficiary Details
          </h2>

          <div className="grid gap-x-8 gap-y-7 lg:grid-cols-2">
            <SearchableDropdown
              label="Payout Bank Account"
              placeholder="Select payout bank account"
              options={payoutBankAccountOptions}
              searchPlaceholder="Search payout accounts"
              value={payoutAccount}
              onChange={(value) => {
                setPayoutAccount(value);
                clearError("payoutAccount");
              }}
              error={errors.payoutAccount}
              required
            />
            <SearchableDropdown
              label="Category"
              placeholder="Select Category"
              options={beneficiaryCategoryOptions}
              searchPlaceholder="Search categories"
              value={category}
              onChange={(value) => {
                setCategory(value);
                clearError("category");
              }}
              error={errors.category}
              required
            />

            <div>
              <FieldLabel required>Mobile Number</FieldLabel>
              <div className="grid grid-cols-[90px_minmax(0,1fr)] gap-4">
                <CountryCodeDropdown />
                <input
                  value={mobileNumber}
                  onChange={(event) => {
                    setMobileNumber(normalizeIndianMobile(event.target.value));
                    clearError("mobileNumber");
                  }}
                  inputMode="numeric"
                  maxLength={10}
                  className={
                    "h-11 rounded-[6px] border bg-white px-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0] " +
                    (errors.mobileNumber ? "border-[#EF4444]" : "border-[#E0E5F2]")
                  }
                  placeholder="Enter mobile number"
                />
              </div>
              {errors.mobileNumber && (
                <p className="mt-2 text-xs font-semibold text-[#EF4444]">
                  {errors.mobileNumber}
                </p>
              )}
            </div>
            <TextInput
              label="Email"
              placeholder="Enter email address"
              value={email}
              onChange={(value) => {
                setEmail(value);
                clearError("email");
              }}
              error={errors.email}
              required
            />
          </div>

          <h2 className="mt-8 mb-5 text-[18px] font-semibold text-[#1B163B]">
            Bank Details
          </h2>
          <div className="grid gap-x-8 gap-y-6 lg:grid-cols-2">
            <TextInput
              label="Bank Account Number"
              placeholder="Enter account number"
              value={accountNumber}
              onChange={(value) => {
                setAccountNumber(normalizeBankAccountNumber(value));
                clearError("accountNumber");
                clearError("confirmAccountNumber");
              }}
              error={errors.accountNumber}
              inputMode="numeric"
              maxLength={18}
              required
            />
            <TextInput
              label="Confirm Account Number"
              placeholder="Re-enter account number"
              value={confirmAccountNumber}
              onChange={(value) => {
                setConfirmAccountNumber(normalizeBankAccountNumber(value));
                clearError("confirmAccountNumber");
              }}
              error={errors.confirmAccountNumber}
              inputMode="numeric"
              maxLength={18}
              required
            />
            <TextInput
              label="IFSC Code"
              placeholder="Enter IFSC code"
              value={ifscCode}
              onChange={(value) => {
                setIfscCode(value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11));
                clearError("ifscCode");
              }}
              error={errors.ifscCode}
              maxLength={11}
              required
            />
            <label className="block">
              <FieldLabel required>Beneficiary Name</FieldLabel>
              {suggestedBeneficiaryName && (
                <div className="mb-2 flex flex-wrap items-center justify-between gap-3 rounded-[6px] border border-[#F59E0B33] bg-[#F59E0B1A] px-3 py-2 text-sm font-medium text-[#1B163B]">
                  <span>
                    Name as per bank records:{" "}
                    <span className="font-semibold">
                      {suggestedBeneficiaryName}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyBeneficiaryName}
                    className="flex items-center gap-2 text-sm font-semibold text-[#6687FF]"
                  >
                    <FiCopy />
                    Copy
                  </button>
                </div>
              )}
              <div
                className={
                  "flex h-11 items-center rounded-[6px] border bg-white px-3 " +
                  (errors.beneficiaryName ? "border-[#EF4444]" : "border-[#E0E5F2]")
                }
              >
                <input
                  value={beneficiaryName}
                  onChange={(event) => {
                    setBeneficiaryName(event.target.value);
                    setBeneficiaryNameVerified(false);
                    setSuggestedBeneficiaryName("");
                    clearError("beneficiaryName");
                  }}
                  className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
                  placeholder="Enter full name as per bank records"
                />
                {beneficiaryNameVerified ? (
                  <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-[#16A34A]">
                    <FiCheckCircle className="text-base" />
                    Verified
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleCheckBeneficiaryName()}
                    disabled={nameCheckLoading}
                    className="shrink-0 text-sm font-semibold text-[#6687FF] disabled:opacity-50"
                  >
                    {nameCheckLoading ? "Checking…" : "Check Now"}
                  </button>
                )}
              </div>
              {errors.beneficiaryName && (
                <p className="mt-2 text-xs font-semibold text-[#EF4444]">
                  {errors.beneficiaryName}
                </p>
              )}
              <p className="mt-2 text-xs font-medium text-[#505E8E]">
                Check the name as per bank records before adding this payee.
              </p>
            </label>
            <SearchableDropdown
              label="Account Type"
              placeholder="Select Account Type"
              options={accountTypeOptions}
              searchPlaceholder="Search account types"
              value={accountType}
              onChange={(value) => {
                setAccountType(value);
                clearError("accountType");
              }}
              error={errors.accountType}
              required
            />
            <TextInput
              label="Branch Name"
              placeholder="Enter branch name"
              value={branchName}
              onChange={(value) => {
                setBranchName(value);
                clearError("branchName");
              }}
              error={errors.branchName}
              required
            />
          </div>

          <h2 className="mt-8 mb-5 text-[18px] font-semibold text-[#1B163B]">
            Additional Information{" "}
            <span className="text-sm font-medium text-[#505E8E]">
              (Optional)
            </span>
          </h2>
          <div className="grid gap-x-8 gap-y-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
            <label className="block">
              <FieldLabel>Purpose / Remarks</FieldLabel>
              <textarea
                value={remarks}
                onChange={(event) => setRemarks(event.target.value.slice(0, 150))}
                className="h-[72px] w-full resize-none rounded-[6px] border border-[#E0E5F2] bg-white px-3 py-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
                placeholder="Add remarks or purpose for adding this beneficiary"
                maxLength={150}
              />
              <p className="mt-2 text-right text-xs font-medium text-[#505E8E]">
                {remarks.length} / 150
              </p>
            </label>
            <label className="block">
              <FieldLabel>Tags (Optional)</FieldLabel>
              <input
                value={tags}
                onChange={(event) => setTags(event.target.value)}
                className="h-11 w-full rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
                placeholder="Enter tags"
              />
              <p className="mt-2 text-xs font-medium text-[#505E8E]">
                Separate multiple tags with comma (,)
              </p>
            </label>
          </div>

          <div className="mt-7 flex items-center gap-3 text-sm font-medium text-[#505E8E]">
            <FiShield className="text-lg text-[#6687FF]" />
            All beneficiary details are encrypted and stored securely.
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
            Beneficiary Summary
          </h2>
          <div className="mb-4 h-px bg-[#E0E5F2]" />

          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F4F7FE] text-lg font-semibold text-[#505E8E]">
              -
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1B163B]">
                -
              </p>
              <span className="mt-2 inline-flex rounded-[5px] bg-[#EDE9FE] px-2 py-1 text-xs font-semibold text-[#6D28D9]">
                New Beneficiary
              </span>
            </div>
          </div>

          <div className="mb-5 h-px bg-[#E0E5F2]" />
          <h3 className="mb-3 text-sm font-semibold text-[#1B163B]">
            Bank Details
          </h3>
          <SummaryLine label="Account Number" />
          <SummaryLine label="IFSC Code" />
          <SummaryLine label="Beneficiary Name" />
          <SummaryLine label="Account Type" />
          <SummaryLine label="Branch" />

          <div className="my-5 h-px bg-[#E0E5F2]" />
          <h3 className="mb-3 text-sm font-semibold text-[#1B163B]">
            Contact Details
          </h3>
          <SummaryLine label="Mobile Number" />
          <SummaryLine label="Email" />

          <div className="mt-6 flex gap-4 rounded-[6px] bg-[#6687FF1A] px-5 py-4 text-sm font-medium text-[#6687FF]">
            <FiInfo className="mt-1 shrink-0 text-lg" />
            <p>Beneficiary will be available for payouts once verified.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default PayoutAddBeneficiary;
